import {
  NavigationProp,
  useFocusEffect,
  useIsFocused,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useGlobalConfig } from 'context/GlobalContext';
import { usePin } from 'context/PinContext';
import { useSelectedProfile } from 'hooks/useSelectedProfile';
import { RootStackScreenProps } from 'navigation/AppNavigator';
import { RootStackParamList } from 'navigation/AppNavigator.web';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import ErrorHandler, { ErrorHandlerType } from 'screens/ErrorHandler';
import TransmitionCatch from 'screens/TransmitionCatch';
import { useGetProgramQuery } from 'services/cms';
import { CmsProgram } from 'types';
import { AGE_RATINGS } from 'utils/ageRating';
import { ProgramType } from 'utils/enum';
import { isEventTimePassed } from 'utils/index';
import { Platform } from 'utils/platform';

import ExternalProgram from './ExternalProgram';
import LiveScreen from './LiveScreen';
import MovieScreen from './MovieScreen';
import PodcastScreen from './PodcastScreen';
import TvPodcastScreen from './PodcastScreen.ctv';
import RadioScreen from './RadioScreen';
import RestrictedScreen from './RestrictedScreen';
import SeasonalScreen from './SeasonalScreen';

import FocusView from 'components/FocusView';
import Header from 'components/Header';
import useSuperiorNavbar from 'components/Header/helpers/useSuperiorNavbar';
import LiveMenuProvider from 'components/LiveMenu';
import { NoCopyright } from 'components/NoCopyright';
import { usePlayer } from 'components/Player/context';
import ProgramContext from 'components/Program/context';
import { WatchOnApp } from 'components/WatchOnApp';
import { WatchOnApp as WatchOnAppCTV } from 'components/WatchOnApp/index.ctv';
import IsLoading from 'components/isLoading';

type ProgramScreenProps = RootStackScreenProps<'Program'> & {
  program: CmsProgram;
  error: FetchBaseQueryError | SerializedError | undefined;
};

const headerNavigation = (hasSuperiorNavbar: boolean) => {
  if (Platform.isTV) {
    return <></>;
  }

  return (
    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
      <Header transparent={!hasSuperiorNavbar} />
    </View>
  );
};

const ProgramScreen: React.FC<ProgramScreenProps> = (props) => {
  const { program } = props;
  const route = useRoute();
  const hasSuperiorNavbar = useSuperiorNavbar(route);
  const { lastWatchedMedia } = usePlayer();
  const { isRestrictedProfile, isFetching, selectedProfile } = useSelectedProfile();

  if (isFetching) {
    return (
      <View style={styles.loadingContainer}>
        <IsLoading />
      </View>
    );
  }

  const userRatingIndex = AGE_RATINGS.indexOf(selectedProfile?.ageRating as any);
  const mediaRatingIndex = AGE_RATINGS.indexOf(program?.rating as any);

  const isAgeRestricted = userRatingIndex < mediaRatingIndex;

  const isVerticalNovelUnavailable =
    (Platform.OS === 'web' || Platform.isTV) &&
    program.category.some((tag) => tag.id === 'novelas-verticais');

  if (isVerticalNovelUnavailable) {
    return (
      <FocusView key={program._id} trapFocusUp trapFocusRight style={{ flex: 1 }}>
        {Platform.isTV ? <WatchOnAppCTV /> : <WatchOnApp />}
      </FocusView>
    );
  }

  const screens: { [key in CmsProgram['type']]?: React.FC<RootStackScreenProps<'Program'>> } = {
    [ProgramType.LIVE as string]: isRestrictedProfile ? RestrictedScreen : LiveScreen,
    [ProgramType.MOVIE as string]: MovieScreen,
    [ProgramType.RADIO as string]: RadioScreen,
    [ProgramType.PODCAST as string]: Platform.isTV ? TvPodcastScreen : PodcastScreen,
    [ProgramType.DOCUMENTARY as string]: SeasonalScreen,
    [ProgramType.PROGRAM as string]: SeasonalScreen,
    [ProgramType.TV_SHOW as string]: SeasonalScreen,
    [ProgramType.EXTERNALPROGRAM]: ExternalProgram,
  };

  if (
    program.type === ProgramType.MOVIE &&
    lastWatchedMedia &&
    program._id !== lastWatchedMedia?.programId
  ) {
    return null;
  }

  const Screen = isAgeRestricted ? RestrictedScreen : screens[program.type];
  if (!Screen) return null;

  const verifyTransmission = () => {
    const header = headerNavigation(hasSuperiorNavbar);

    if (program.noCopyright) {
      return (
        <>
          {header}
          <NoCopyright />
        </>
      );
    }

    const content = (
      <>
        {header}
        <Screen {...props} />
      </>
    );

    return program.type === ProgramType.LIVE ? (
      <LiveMenuProvider>{content}</LiveMenuProvider>
    ) : (
      content
    );
  };

  return (
    <FocusView key={program._id} trapFocusUp trapFocusRight style={{ flex: 1 }}>
      <ProgramContext.Provider value={program}>{verifyTransmission()}</ProgramContext.Provider>
    </FocusView>
  );
};

const ProgramScreenPerformaticShield: React.FC<RootStackScreenProps<'Program'>> = (props) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { mute, error, hasTransmissionError, media, reset, setIsVisible } = usePlayer();
  const { setColor } = useGlobalConfig();

  const {
    data: program,
    error: programError,
    isLoading,
    isError,
  } = useGetProgramQuery(props.route.params.programId);
  const isFocused = useIsFocused();
  const { hasInternet } = useGlobalConfig();
  const eventDateStart = program?.eventDateStart;
  const route = useRoute();
  const hasSuperiorNavbar = useSuperiorNavbar(route);
  const header = headerNavigation(hasSuperiorNavbar);
  const isAdultProgram = program?.rating === '18' || program?.rating === 'A18';
  const isAuthorized = useRef(false);
  const { requirePin } = usePin();

  const checkProgramRestriction = useCallback(async () => {
    if (isLoading) return;

    if (program && !isAdultProgram && !program.isBet) {
      isAuthorized.current = true;
      return;
    }

    try {
      const pinSuccess = await requirePin();

      if (pinSuccess) {
        isAuthorized.current = true;
      } else {
        const canGoBack = navigation.canGoBack();
        if (canGoBack) {
          navigation.goBack();
        } else {
          window.location.replace('/');
        }
      }
    } catch (error) {
      navigation.goBack();
    }
  }, [isLoading, program?.id, isAdultProgram, navigation]);

  useEffect(() => {
    checkProgramRestriction();
  }, [checkProgramRestriction]);

  useEffect(() => {
    setIsVisible(false);
  }, [program]);

  useEffect(() => {
    if (media) {
      reset({ media: undefined });
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      setColor(program?.ads?.brandColor);
    }, [program])
  );

  useEffect(() => {
    if (!program) return;

    mute(false);
    setColor(program.ads?.brandColor);
    props.navigation.setOptions({ title: program.title });
  }, [program]);

  const typeMessage = useMemo<ErrorHandlerType | undefined>(() => {
    if (!isFocused || ['ERR_BAD_RESPONSE', 'ERR_BAD_REQUEST'].includes(error?.code as string))
      return undefined;

    if (!program && programError) {
      return ErrorHandlerType.NOT_FOUND;
    }

    if (error === ErrorHandlerType.NETWORK_ERROR) {
      return undefined;
    }

    return error ?? undefined;
  }, [isFocused, program, programError, error, isError, hasInternet]);

  if (isLoading || !isAuthorized.current) {
    return null;
  }

  if (eventDateStart && isEventTimePassed(new Date(eventDateStart))) {
    return (
      <>
        {header}
        <TransmitionCatch startTime={eventDateStart} />
      </>
    );
  }

  if (!program || programError || !!typeMessage || hasTransmissionError) {
    return (
      <>
        <ErrorHandler error={typeMessage} />
      </>
    );
  }

  return <ProgramScreen {...props} program={program} error={programError} />;
};

const styles = {
  loadingContainer: {
    flex: 1,
    justifyContent: 'center' as const,
  },
};

export const heroStyle = StyleSheet.create({
  container: { paddingHorizontal: Platform.isMobileWeb() ? 16 : 0 },
});

export default ProgramScreenPerformaticShield;
